import { Typography } from 'antd';
import { forwardRef } from 'react';

const { Text: AntdText, Link: AntLink, Title: AntTitle, Paragraph: AntParagraph } = Typography;

// Derivados del componente publico en vez de importar de 'antd/es/typography/*'. Endurecimiento
// preventivo, no un arreglo: antd 6 sigue publicando esas rutas (comprobado en la 6.6.5 que fija
// este arbol), pero son internas y no tienen garantia de compatibilidad entre versiones mayores.
//
// Se omiten `ref` y `key` de las props derivadas porque forwardRef vuelve a anadir el `ref` con
// el tipo correcto; dejarlo en el Omit de origen evita que aparezca duplicado.
type AntTextProps = Omit<React.ComponentProps<typeof AntdText>, 'ref' | 'key'>;
type AntTitleProps = Omit<React.ComponentProps<typeof AntTitle>, 'ref' | 'key'>;
type AntLinkProps = Omit<React.ComponentProps<typeof AntLink>, 'ref' | 'key'>;
type AntParagraphProps = Omit<React.ComponentProps<typeof AntParagraph>, 'ref' | 'key'>;

// Los cuatro reenvian el ref A PROPOSITO. antd 6 entrega un ref al hijo de Tooltip, Popover y
// Dropdown para medirlo y posicionarse, y su Trigger ya NO tiene respaldo por findDOMNode: un
// componente funcion que descarte el ref deja al trigger sin nodo, asi que el aviso de consola
// viene acompanado de un popup mal colocado. Estos wrappers se usan como hijo directo de
// Tooltip en una docena de sitios.
type TextRef = React.ElementRef<typeof AntdText>;
type TitleRef = React.ElementRef<typeof AntTitle>;
type LinkRef = React.ElementRef<typeof AntLink>;
type ParagraphRef = React.ElementRef<typeof AntParagraph>;

export interface TextProps extends AntTextProps {
  fontSize?: React.CSSProperties['fontSize'];
  color?: React.CSSProperties['color'];
  fontFamily?: React.CSSProperties['fontFamily'];
  lh?: React.CSSProperties['lineHeight'];
}

const Text = forwardRef<TextRef, TextProps>(({ fontSize, color, fontFamily, lh, ...props }, ref) => {
  if (props.style == null) props.style = {};
  if (fontSize != null) props.style.fontSize = fontSize;
  if (color != null) props.style.color = color;
  if (fontFamily != null) props.style.fontFamily = fontFamily;
  if (lh != null) props.style.lineHeight = lh;

  return <AntdText ref={ref} {...props} />;
});

Text.displayName = 'Text';

export interface TitleProps extends AntTitleProps {
  fontSize?: React.CSSProperties['fontSize'];
  color?: React.CSSProperties['color'];
  fontFamily?: React.CSSProperties['fontFamily'];
  lh?: React.CSSProperties['lineHeight'];
}

const Title = forwardRef<TitleRef, TitleProps>(({ fontSize, color, fontFamily, lh, ...props }, ref) => {
  if (props.style == null) props.style = {};
  if (fontSize != null) props.style.fontSize = fontSize;
  if (color != null) props.style.color = color;
  if (fontFamily != null) props.style.fontFamily = fontFamily;
  if (lh != null) props.style.lineHeight = lh;
  return <AntTitle ref={ref} {...props} />;
});

Title.displayName = 'Title';

export interface LinkProps extends AntLinkProps {
  fontSize?: React.CSSProperties['fontSize'];
  color?: React.CSSProperties['color'];
  fontFamily?: React.CSSProperties['fontFamily'];
  lh?: React.CSSProperties['lineHeight'];
}

const Link = forwardRef<LinkRef, LinkProps>(({ fontSize, color, fontFamily, lh, ...props }, ref) => {
  if (props.style == null) props.style = {};
  if (fontSize != null) props.style.fontSize = fontSize;
  if (color != null) props.style.color = color;
  if (fontFamily != null) props.style.fontFamily = fontFamily;
  if (lh != null) props.style.lineHeight = lh;
  return <AntLink ref={ref} {...props} />;
});

Link.displayName = 'Link';

export interface ParagraphProps extends AntParagraphProps {
  lh?: React.CSSProperties['lineHeight'];
  mt?: React.CSSProperties['marginTop'];
  mb?: React.CSSProperties['marginBottom'];
}

const Paragraph = forwardRef<ParagraphRef, ParagraphProps>(({ lh, mt, mb, ...props }, ref) => {
  if (props.style == null) props.style = {};
  if (lh != null) props.style.lineHeight = lh;
  if (mt != null) props.style.marginTop = mt;
  if (mb != null) props.style.marginBottom = mb;
  return <AntParagraph ref={ref} {...props} />;
});

Paragraph.displayName = 'Paragraph';

export { Text, Title, Link, Paragraph };
